import React, { useMemo } from 'react';
import { NavigationConfig } from './types';
import { cn } from '../../../utils';
import { TooltipProvider } from '../../ui/tooltip';
import { AppSidebarSection } from './app-sidebar-section';
import { AppSidebarBottomSection } from './app-sidebar-bottom-section';
import { SIDEBAR_DIMENSIONS, ANIMATION_DURATION } from './constants';
import { filterNavigationConfig } from './utils/permission-filter';
import { UserType } from '../../../../types';

interface AppSidebarExtendedProps {
  config: NavigationConfig;
  pathname: string;
  tenantId: string;
  user?: UserType | null;
  onNavigate?: (href: string, isExternal: boolean) => void;
}

export function AppSidebarExtended({
  config,
  pathname,
  tenantId,
  user,
  onNavigate,
}: AppSidebarExtendedProps) {
  // Filter navigation items based on permissions
  const { filteredSections, filteredBottomLinks } = useMemo(
    () => filterNavigationConfig(config, user || null, tenantId),
    [config, user, tenantId]
  );

  const sidebarStyle = useMemo(
    () => ({
      width: `${SIDEBAR_DIMENSIONS.EXTENDED_WIDTH_PX}px`,
      flexShrink: 0,
      transition: `width ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    }),
    []
  );

  const sections = useMemo(
    () => (
      <div className="tw-flex tw-flex-col" style={{ gap: '12px' }}>
        {filteredSections.map((section, index) => (
          <AppSidebarSection
            key={index}
            section={section}
            isCollapsed={false}
            pathname={pathname}
            onNavigate={onNavigate}
            isExtended={true}
          />
        ))}
      </div>
    ),
    [filteredSections, pathname, onNavigate]
  );

  const bottomLinks = useMemo(() => {
    if (!filteredBottomLinks || filteredBottomLinks.length === 0) {
      return null;
    }

    return (
      <AppSidebarBottomSection
        items={filteredBottomLinks}
        isCollapsed={false}
        pathname={pathname}
        onNavigate={onNavigate}
        isExtended={true}
      />
    );
  }, [filteredBottomLinks, pathname, onNavigate]);

  return (
    <TooltipProvider delayDuration={100}>
      <aside
        className={cn(
          'tw-flex tw-flex-col tw-bg-white tw-text-gray-900 tw-border-r tw-box-border',
          'tw-flex-shrink-0'
        )}
        style={{
          ...sidebarStyle,
          borderRightColor: '#e5e7eb',
        }}
      >
        <div
          className="tw-h-full tw-flex tw-flex-col"
          style={{
            overflowY: 'hidden',
            overflowX: 'visible',
          }}
        >
          <nav
            className="tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-flex tw-flex-col"
            style={{
              paddingTop: '16px',
              paddingBottom: '16px',
              paddingLeft: '12px',
              paddingRight: '12px',
            }}
          >
            {sections}
            {bottomLinks}
          </nav>
        </div>
      </aside>
    </TooltipProvider>
  );
}
